/* eslint-disable max-len */
/* eslint-disable jsx-a11y/media-has-caption */
import { Button, CircularProgress, Grid, makeStyles, Paper, Theme } from '@material-ui/core';
import { ApiError, ModeAppProxyAPI, SortOrder } from '@moderepo/mode-apis';
import { capitalizeAll, GenericMessageBox, getErrorMessage, ITEMS_PER_PAGE_10 } from '@moderepo/mode-app-components';
import { modeUIContext, showErrorNotification } from '@moderepo/mode-ui-state';
import clsx from 'clsx';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import produce, { Draft } from 'immer';
import { ExtGrid, GridCustomSettings, isGridCustomSettings, EntityUIComponentProps } from '../../../..';
import { useSelectEntity, useSelectProject } from '../../../../../utils/dataStateHooks';
import { useRitzTranslation } from '../../../../../utils/customHooks';
import { RitzComponentsCollection } from '../../../../../utils/ritzComponentsCollection';
import { EntityUIComponentCollection, getMetricsDefinitionMetricsId } from '../../..';
import { ActlyzerEventVideoCard, ActlyzerEventVideoInfo, ActlyzerVideoEventLogData, DEFAULT_EVENT_DATA_NAME, DEFAULT_EVENT_RESOURCE_PATH } from '.';
import { isObjectLiteral, isValidSettingNumberValue, isValidSettingStringValue } from '../../../../../utils/utils';
import { useConvertTimeControlParamsToFetchDataTimeRangeParams } from '../..';
import { SettingsErrorLogger } from '../../../../../utils/logger';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
        },

        videoCard: {
            height: '100%',
        },

        loadMoreButtonContainer: {
            width         : '100%',
            display       : 'flex',
            alignItems    : 'center',
            justifyContent: 'center',
            marginTop     : theme.spacing(3),
        },

        loadMoreButton: {
        },
    };
}, { name: 'ActlyzerEntityEventVideosViewUIComponent', index: 1 });



/**
 * This is the interface for the ActlyzerEntityEventVideosViewUIComponent's CustomSettings.
 */
interface ActlyzerEntityEventVideosViewUIComponentCustomSettings extends GridCustomSettings {
    // The event data name. The default value is "act_event_data". This will be used for creating the searchKeys when searching for events.
    readonly eventDataName?: string | undefined;

    // The event resource path where even data can be fetched. The default value is "/actlyzer/events"
    readonly eventsResourcePath?: string | undefined;

    // The custom pagination limit to use when fetching events. The default is 10. The range is any number between 5 and 100.
    readonly fetchEventsLimit?: number | undefined;

    // To set the sort order of the videos. The default will be DESC (latest videos show first) if this is not provided.
    readonly videosSortOrder?: SortOrder;
}


/**
 * Type guard function to validate the obj to make sure it is a type of ActlyzerEntityEventVideosViewUIComponentCustomSettings
 */
const isActlyzerEntityEventVideosViewUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is ActlyzerEntityEventVideosViewUIComponentCustomSettings => {
    const settings = obj as ActlyzerEntityEventVideosViewUIComponentCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.eventDataName)) {
        return errorLogger?.logError({
            message: '"eventDataName" must be undefined or a non-empty string.',
            value  : settings.eventDataName,
            path   : [...configPath, 'eventDataName'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.eventsResourcePath)) {
        return errorLogger?.logError({
            message: '"eventsResourcePath" must be undefined or a non-empty string.',
            value  : settings.eventsResourcePath,
            path   : [...configPath, 'eventsResourcePath'],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.fetchEventsLimit, false, false, 5, 100)) {
        return errorLogger?.logError({
            message: '"fetchEventsLimit" must be undefined or an integer between 5 and 100.',
            value  : settings.fetchEventsLimit,
            path   : [...configPath, 'fetchEventsLimit'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.videosSortOrder, false, false, Object.values(SortOrder))) {
        return errorLogger?.logError({
            message: `"videosSortOrder" must be undefined or one of the following values [${Object.values(SortOrder).map((value) => {
                return `"${value}"`;
            }).join(', ')}].`,
            value: settings.videosSortOrder,
            path : [...configPath, 'videosSortOrder'],
        }) ?? false;
    }

    if (!isGridCustomSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    return true;
};


/**
 * Call the ModeAppProxyAPI.getEventLog API and cast the response to ActlyzerVideoEventLogData. We can assume the response will be an
 * an EventLogData of specific type ActlyzerVideoEventLogData
 */
const fetchEventLogs = async (
    homeId: number,
    logResourcePath: string,
    metricsDefinitionMetricsId: string,
    startTime: number,
    endTime: number,
    pageSize: number,
    currentPage: number,
    sortOrder: SortOrder,
): Promise<ActlyzerVideoEventLogData> => {
    const response = await ModeAppProxyAPI.getInstance().getEventLog(homeId, logResourcePath, {
        begin     : (new Date(startTime)).toISOString(),
        end       : (new Date(endTime)).toISOString(),
        searchKeys: metricsDefinitionMetricsId,
        limit     : pageSize,
        skip      : currentPage * pageSize,
        sortBy    : sortOrder,
    });

    return response as ActlyzerVideoEventLogData;
};



interface ActlyzerEntityEventVideosViewUIComponentProps extends EntityUIComponentProps {
    readonly customSettings?: ActlyzerEntityEventVideosViewUIComponentCustomSettings | undefined;
}


/**
 * This EntityUIComponent is used for showing an entity basic info e.g. name, description, image, and icon
 */
export const ActlyzerEntityEventVideosViewUIComponent: React.FC<ActlyzerEntityEventVideosViewUIComponentProps> = (
    props: ActlyzerEntityEventVideosViewUIComponentProps,
) => {

    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const classes = useStyle();
    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);

    const selectedEntity = useSelectEntity(props.projectId, props.entityId, false);


    /**
     * To fetch event logs, we will need these info, homeId, logResourcePath from the metricsDef's settings, and the metricsDefinitionMetricsId.
     * These info will change when the selectedEntity changed so we need to get new value each time the selectedEntity changed
     */
    const { homeId, logResourcePath, metricsDefinitionMetricsId } = useMemo(() => {
        if (selectedEntity) {

            // If we find the metricsDefinition then get the info from the metrics definition that can be use to make API call to fetch event log
            return {
                homeId                    : selectedEntity.homeId,
                logResourcePath           : props.customSettings?.eventsResourcePath ?? DEFAULT_EVENT_RESOURCE_PATH,
                metricsDefinitionMetricsId: getMetricsDefinitionMetricsId(
                    selectedEntity, props.customSettings?.eventDataName ?? DEFAULT_EVENT_DATA_NAME,
                ),
            };
        }

        return {
            homeId: undefined, logResourcePath: undefined, metricsDefinitionMetricsId: undefined,
        };
    }, [props.customSettings, selectedEntity]);



    /**
     * Create the params that are required for fetching data based on the props.fetchDataParams. Since this component need to fetch data
     * based on some start/end time, it will convert the props.fetchDataParams to FetchDataByTimeRangeOptions.
     */
    const fetchDataOptions = useConvertTimeControlParamsToFetchDataTimeRangeParams(props.fetchDataParams);



    /**
     * The state to keep track of the video events data we fetched
     */
    const [videoEventsData, setVideoEventsData] = useState<{
        readonly startTime?: number | undefined;
        readonly endTime?: number | undefined;

        // Used for pagination
        readonly limit: number;
        readonly currentPage: number;
        readonly sortOrder: SortOrder;
        readonly totalEvents: number;

        // The videos info we got from the API call response
        readonly videosInfo: readonly ActlyzerEventVideoInfo[],
    }>({
        // Get the limit from the customSettings if there is one. If not, set it to 10.
        limit      : props.customSettings?.fetchEventsLimit !== undefined ? Math.floor(props.customSettings.fetchEventsLimit) : ITEMS_PER_PAGE_10,
        currentPage: 0,
        sortOrder  : props.customSettings?.videosSortOrder ?? SortOrder.DESC,
        totalEvents: 0,
        videosInfo : [],
    });


    /**
     * Whenever the fetchDataOptions, selectedEntity changed, we will need to reset the current video event data e.g. currentPage, videos, etc...
     * because it will be a different set of videos.
     */
    useEffect(() => {
        if (homeId && logResourcePath && metricsDefinitionMetricsId && fetchDataOptions) {
            setVideoEventsData((currentValue) => {
                return produce(currentValue, (draft: Draft<typeof currentValue>) => {
                    draft.startTime = fetchDataOptions.startTime;
                    draft.endTime = fetchDataOptions.endTime;
                    draft.currentPage = 0;
                    draft.totalEvents = 0;
                    draft.videosInfo = [];
                });
            });
        }
    }, [fetchDataOptions, homeId, logResourcePath, metricsDefinitionMetricsId]);



    /**
     * This useEffect is used for fetching data whenever some of the state changed e.g. startTime, endTime, currentPage, etc...
     */
    useEffect(() => {
        if (homeId && logResourcePath && metricsDefinitionMetricsId && videoEventsData.startTime !== undefined && videoEventsData.endTime !== undefined) {

            // Make API call to fetch event log data for the specified params
            fetchEventLogs(
                homeId,
                logResourcePath,
                metricsDefinitionMetricsId,
                videoEventsData.startTime,
                videoEventsData.endTime,
                videoEventsData.limit,
                videoEventsData.currentPage,
                videoEventsData.sortOrder,
            ).then((eventLogData) => {

                // Once done, parse the data and create ActlyzerEventVideoInfo objects and add them to the current array of videos.
                const videos: ActlyzerEventVideoInfo[] = [];

                (eventLogData.act_event_list ?? []).forEach((event) => {
                    if (event.Videos) {
                        event.Videos.forEach((video) => {
                            // Create 1 ActlyzerEventVideoInfo for each video in the record.videos and add it to the result
                            videos.push({
                                eventKey : event.Payload.EventKey,
                                startTime: event.Payload.StartTime,
                                endTime  : event.Payload.EndTime,
                                cameraId : video.CamID,
                                thumbnail: video.ThumbnailURL,
                                videoUrl : video.VideoURL,
                            });
                        });
                    } else {
                        // This event does not have videos. Add 1 ActlyzerEventVideoInfo object to the "videos" array but without video info
                        videos.push({
                            eventKey : event.Payload.EventKey,
                            startTime: event.Payload.StartTime,
                            endTime  : event.Payload.EndTime,
                        });
                    }
                });

                // Update the event data state
                setVideoEventsData((currentValue) => {
                    return produce(currentValue, (draft: Draft<typeof currentValue>) => {
                        draft.totalEvents = eventLogData.total;
                        draft.videosInfo.push(...videos);
                    });
                });

            }).catch((error) => {
                uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));
            });
        }
    }, [videoEventsData.currentPage, videoEventsData.limit, homeId, logResourcePath, metricsDefinitionMetricsId, uiDispatch,
        videoEventsData.sortOrder, videoEventsData.startTime, videoEventsData.endTime]);



    /**
     * On "Load More" button click, we will increment the "currentPage" state. This will trigger the useEffect to fetch data for the new currentPage
     */
    const loadMoreEvents = useCallback(() => {
        setVideoEventsData((currentValue) => {
            return produce(currentValue, (draft: Draft<typeof currentValue>) => {
                draft.currentPage = currentValue.currentPage + 1;
            });
        });
    }, []);


    /**
     * Check if there is more events to load. We know the total events, the currentPage, and the pageSize therefore we can tell if there are more
     * events to load. We will use this flag to enable the "Load More" button.
     */
    const hasMoreEvents = useMemo(() => {
        if (videoEventsData && videoEventsData.currentPage !== undefined && videoEventsData.totalEvents !== undefined) {
            const numberOfPages = Math.ceil(videoEventsData.totalEvents / videoEventsData.limit);
            return numberOfPages - 1 > videoEventsData.currentPage;
        }
        return false;
    }, [videoEventsData]);


    return (
        <>
            {selectedEntity && videoEventsData.videosInfo.length > 0 && (
                <>
                    <ExtGrid
                        className={clsx(props.className, classes.root)}
                        container
                        xs_spacing={1}
                        md_spacing={2}
                        lg_spacing={3}
                    >
                        {videoEventsData.videosInfo.map((video) => {
                            return (
                                <Grid
                                    key={`${video.eventKey}-${video.cameraId}`}
                                    item
                                    xs={props.customSettings?.gridSizes?.xs ?? 12}
                                    sm={props.customSettings?.gridSizes?.sm}
                                    md={props.customSettings?.gridSizes?.md}
                                    lg={props.customSettings?.gridSizes?.lg}
                                    xl={props.customSettings?.gridSizes?.xl}
                                >
                                    <ActlyzerEventVideoCard
                                        className={classes.videoCard}
                                        projectId={props.projectId}
                                        videoInfo={video}
                                    />
                                </Grid>
                            );
                        })}
                    </ExtGrid>
                    {/* Add the "Load More" button if there are more events to load */}
                    {hasMoreEvents && (
                        <div className={classes.loadMoreButtonContainer}>
                            <Button
                                className={classes.loadMoreButton}
                                variant="contained"
                                color="primary"
                                onClick={loadMoreEvents}
                            >
                                {trans('label.load_more')}
                            </Button>
                        </div>
                    )}
                </>
            )}

            {selectedEntity && (!videoEventsData.videosInfo || videoEventsData.videosInfo.length === 0) && (
                <Paper elevation={2}>
                    <GenericMessageBox
                        title={trans('generic.no_data.title', {
                            term: capitalizeAll(trans('label.video', { count: 2 })),
                        })}
                    />
                </Paper>
            )}

            {!selectedEntity && (
                <CircularProgress />
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent(
    'ActlyzerEntityEventVideosViewUIComponent', ActlyzerEntityEventVideosViewUIComponent,
);



// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Actlyzer_Entity_Event_Videos_Comp',
    {
        componentFunc          : ActlyzerEntityEventVideosViewUIComponent,
        customSettingsValidator: isActlyzerEntityEventVideosViewUIComponentCustomSettings,
    },
);
